import type { Maybe } from '@@types/helpers'
import StaticError from '@app/pages/Error/StaticError'
import { StoreRoot } from '@app/stores'
import type { IStores } from '@app/stores/types'
import type { HardReloadError } from '@libs/errors'
import { isErrorOfType } from '@libs/errors/functions'
import { ErrorName } from '@libs/errors/types'
import { getLogger } from '@libs/logger'
import Root from '../components/Root'
import { extractProfileNameFromUrl, setProfileNameInRouter } from '../utils'
import { fetchPostLoginData } from '../utils/fetchData'
import { setupEnvironment } from '../utils/setupEnvironment'
import { initializeApp } from './initializeApp'
import { initializeTul } from './initializeTul'

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    ROOT_STORE: StoreRoot
  }
}

const logger = getLogger()

/**
 * Create the app and return the Root component or a StaticError component error.
 */
export function createApp(parameters: {
  pathname: string
  csrfToken: Maybe<string>
}): Promise<Maybe<JSX.Element>> {
  return (
    Promise.resolve()
      /**
       * Setup environment.
       */
      .then(() => setupEnvironment(parameters))

      /**
       * Expose the storeRoot into the DOM (for debugging purposes).
       */
      .then(environment => {
        // retrieve the root store from window first (hot reloading)
        const storeRoot =
          window.ROOT_STORE ||
          new StoreRoot(environment).instanciateGraphQLRequestors()

        window.ROOT_STORE = storeRoot

        return storeRoot
      })

      /**
       * Initialize the app.
       */
      .then(async storeRoot => {
        const profileName = extractProfileNameFromUrl(parameters.pathname)

        await initializeApp(storeRoot)({
          pathname: parameters.pathname,
          profileName
        })

        return storeRoot
      })

      /**
       * Initialize default parameters of the router.
       */
      .then(storeRoot => {
        const { storeAuthentication } = storeRoot.stores

        // set the profileName as default parameter into the router
        setProfileNameInRouter(storeAuthentication)

        return storeRoot
      })

      .then(async storeRoot => {
        await fetchPostLoginData(storeRoot)
        return storeRoot
      })

      /**
       * Init TUL
       *        */
      .then(async storeRoot => {
        const tulApi = await initializeTul(storeRoot)
        storeRoot.setTulApi(tulApi)

        return storeRoot
      })

      .then(storeRoot => {
        storeRoot.stores.storeAlerts.storeAlertsIoA.fetchAlerts(undefined, {
          forceCount: true
        })

        return storeRoot
      })

      /**
       * Render the app by passing all instantiated stores for the MobX provider.
       */
      .then(storeRoot => {
        const stores: IStores & { storeRoot: StoreRoot } = {
          ...storeRoot.stores,
          storeRoot
        }

        // Initialize the current pathname in the StoreLayout
        storeRoot.stores.storeLayout.setCurrentPathname(
          document.location.pathname
        )

        return <Root stores={stores} />
      })

      /**
       * Handle errors and redirections.
       */
      .catch(err => {
        // handle hard redirections
        if (isErrorOfType<HardReloadError>(err, ErrorName.HardReloadError)) {
          const url = err.getMessage()

          const logMessage = url ? `Redirect to ${url}` : 'Refresh the page'
          logger.info(`[AFAD] ${logMessage}.`)
          logger.debug(err)

          if (!url) {
            document.location.reload()
          } else {
            document.location.href = url
          }

          // return null to avoid to render the App
          return Promise.resolve(null)
        }

        const message = (
          <>
            <div>The application encountered an error and has stopped.</div>
            <div>
              Please refresh or contact your Tenable Identity Exposure
              administrator if the problem continues.
            </div>
          </>
        )

        return <StaticError reason={message} />
      })
  )
}
